/**
 * Backend URL utility for Superedit
 */
export const getBackendUrl = (): string => {
    // 1. Check if VITE_BACKEND_URL is defined in .env
    if (import.meta.env.VITE_BACKEND_URL) {
        return import.meta.env.VITE_BACKEND_URL.replace(/\/$/, '');
    }

    // 2. Fallback based on current hostname
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    if (isLocal) {
        // In local dev, prefer same-origin proxy instead of hard-coding a backend port.
        return '/api';
    }

    // 3. Final production fallback
    return 'https://cenema-backend.okstudio.workers.dev';
};

export const getBackendFallbackUrls = (): string[] => {
    const primary = getBackendUrl();
    const fallbacks = [primary];
    const production = 'https://cenema-backend.okstudio.workers.dev';

    if (primary !== production) {
        fallbacks.push(production);
    }

    return [...new Set(fallbacks.map((url) => url.replace(/\/$/, '')))];
};

export const toAbsoluteBackendUrl = (url: string | undefined): string => {
    if (!url) return '';

    if (/^https?:\/\//i.test(url) || url.startsWith('blob:') || url.startsWith('data:')) {
        return url;
    }

    const backend = getBackendUrl();

    if (url.startsWith('/api/')) {
        return `${backend}${url.slice(4)}`;
    }

    if (url.startsWith('/v1/')) {
        return `${backend}${url}`;
    }

    return url;
};

export const unwrapProxyUrl = (url: string | undefined): string => {
    if (!url) return '';

    let current = url;
    let guard = 0;

    while (current.includes('/v1/proxy-image?url=') && guard < 5) {
        try {
            const parsed = new URL(current, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
            const nested = parsed.searchParams.get('url');
            if (!nested) break;
            current = decodeURIComponent(nested);
            guard += 1;
        } catch {
            break;
        }
    }

    return current;
};

export const isPublicR2Url = (url: string): boolean =>
    /^https?:\/\/pub-[a-z0-9]+\.r2\.dev\//i.test(url);

/**
 * Wraps a URL with the backend proxy if it's an R2 or potentially CORS-problematic URL
 */
export const getProxyUrl = (url: string | undefined): string => {
    if (!url) return '';
    const unwrappedUrl = unwrapProxyUrl(url);
    const normalizedUrl = toAbsoluteBackendUrl(unwrappedUrl);

    // Return as is if already a proxy URL or data/blob URL
    if (normalizedUrl.includes('proxy-image') || normalizedUrl.startsWith('blob:') || normalizedUrl.startsWith('data:')) {
        return normalizedUrl;
    }

    if (isPublicR2Url(normalizedUrl)) {
        return normalizedUrl;
    }

    const backend = getBackendUrl();

    // Audio assets should stay on their original URL.
    if (/\.(wav|mp3|aac|m4a|ogg)(\?|$)/i.test(normalizedUrl) || normalizedUrl.includes('/tts/')) {
        return normalizedUrl;
    }

    // Only proxy R2 image/video-style assets that need backend mediation.
    if (normalizedUrl.includes('r2.dev')) {
        return `${backend}/v1/proxy-image?url=${encodeURIComponent(normalizedUrl)}`;
    }

    return normalizedUrl;
};
